"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CheckCircle2, Loader2, MapPin, Search, X } from "lucide-react";
import { cn, type PickedLocation } from "@routecore/core";
import { Modal, ModalHeader } from "@routecore/modal";
import { GhostButton, inputCls, PrimaryButton } from "@routecore/controls";

type SearchHit = { display_name: string; lat: string; lon: string };

const FALLBACK_CENTER: [number, number] = [14.5995, 120.9842]; // Manila — matches seed territory

const PIN_HTML = `
<div style="position:relative;width:24px;height:34px">
  <div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:22px;height:22px;border-radius:50%;background:#E4572E;border:3px solid #FDFBF6;box-shadow:0 5px 12px rgba(34,29,21,.4)"></div>
  <div style="position:absolute;left:50%;top:20px;transform:translateX(-50%);width:3px;height:12px;background:#E4572E;border-radius:2px"></div>
</div>`;

function shortLabel(display: string) {
  return display
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
}

export default function MapPickerModal({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: PickedLocation | null;
  onClose: () => void;
  onSave: (loc: PickedLocation) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(null);
  const [label, setLabel] = useState("");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);

  /* ------------------------------ state reset ------------------------------ */
  useEffect(() => {
    if (!open) return;
    setPin(initial ? { lat: initial.lat, lng: initial.lng } : null);
    setLabel(initial?.name ?? "");
    setQuery("");
    setHits([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ------------------------- reverse geocode helper ------------------------ */
  const reverseName = useCallback(async (lat: number, lng: number) => {
    setLocating(true);
    try {
      const res = await fetch(`/api/routecore/geocode?lat=${lat}&lng=${lng}`);
      const data = (await res.json()) as { display_name?: string };
      setLabel(data?.display_name ? shortLabel(data.display_name) : "Dropped pin");
    } catch {
      setLabel("Dropped pin");
    } finally {
      setLocating(false);
    }
  }, []);

  /* ------------------------------- map init -------------------------------- */
  useEffect(() => {
    if (!open || mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: false,
      worldCopyJump: true,
    });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    if (initial) map.setView([initial.lat, initial.lng], 14);
    else map.setView(FALLBACK_CENTER, 11);

    map.on("click", (e: L.LeafletMouseEvent) => {
      const p = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPin(p);
      reverseName(p.lat, p.lng);
    });

    mapRef.current = map;
    // Modal springs open — let Leaflet re-measure once it settles.
    const t1 = setTimeout(() => map.invalidateSize(), 260);
    const t2 = setTimeout(() => map.invalidateSize(), 600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ------------------------------ pin marker ------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pin) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    const icon = L.divIcon({ className: "", html: PIN_HTML, iconSize: [24, 34], iconAnchor: [12, 33] });
    if (markerRef.current) {
      markerRef.current.setLatLng([pin.lat, pin.lng]);
      markerRef.current.setIcon(icon);
    } else {
      markerRef.current = L.marker([pin.lat, pin.lng], { icon, keyboard: false }).addTo(map);
    }
    map.panTo([pin.lat, pin.lng], { animate: true });
  }, [pin]);

  /* ------------------------------- search ---------------------------------- */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setHits([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/routecore/geocode?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setHits(Array.isArray(data) ? (data as SearchHit[]).slice(0, 6) : []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 380);
    return () => clearTimeout(t);
  }, [query]);

  const chooseHit = (hit: SearchHit) => {
    const lat = Number(hit.lat);
    const lng = Number(hit.lon);
    if (!isFinite(lat) || !isFinite(lng)) return;
    setPin({ lat, lng });
    setLabel(shortLabel(hit.display_name));
    setHits([]);
    setQuery("");
    mapRef.current?.flyTo([lat, lng], 15, { duration: 0.8 });
  };

  const clearPin = () => {
    setPin(null);
    setLabel("");
    markerRef.current?.remove();
    markerRef.current = null;
  };

  const save = () => {
    if (!pin) return;
    onSave({
      name: label.trim() || "Pinned location",
      lat: Number(pin.lat.toFixed(6)),
      lng: Number(pin.lng.toFixed(6)),
    });
  };

  return (
    <Modal open={open} onClose={onClose} zIndex={85} panelClassName="max-w-xl">
      <ModalHeader eyebrow="Location" title="Pin the stop" onClose={onClose} />

      {/* search */}
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-ink-faint" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for a place — city, street, landmark…"
          className={cn(inputCls, "pr-10 pl-10")}
        />
        {searching && (
          <Loader2 className="absolute top-1/2 right-3.5 size-4 -translate-y-1/2 animate-spin text-ink-faint" />
        )}
        {hits.length > 0 && (
          <ul className="absolute right-0 left-0 z-[1000] mt-1.5 overflow-hidden rounded-xl border border-line bg-porcelain shadow-lifted">
            {hits.map((h, i) => (
              <li key={`${h.lat}-${h.lon}-${i}`}>
                <button
                  type="button"
                  onClick={() => chooseHit(h)}
                  className="flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-cream"
                >
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-accent" />
                  <span className="text-xs leading-snug font-medium text-ink">{h.display_name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* map */}
      <div className="relative mt-3">
        <div ref={containerRef} className="h-[300px] w-full overflow-hidden rounded-2xl border border-line sm:h-[340px]" />
      </div>

      {/* label + coords */}
      <div className="mt-3 flex items-end gap-3">
        <label className="block flex-1">
          <span className="mb-1.5 block text-[11px] font-bold tracking-[0.14em] text-ink-soft uppercase">
            Location name
          </span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={pin ? "Name this stop…" : "Drop a pin to name this stop"}
            className={inputCls}
          />
        </label>
        <div className="shrink-0 pb-2.5 text-right text-[11px] leading-tight font-semibold text-ink-faint tabular-nums">
          {pin ? (
            <>
              {pin.lat.toFixed(5)}
              <br />
              {pin.lng.toFixed(5)}
            </>
          ) : (
            <>
              no pin
              <br />
              yet
            </>
          )}
        </div>
      </div>

      {/* actions */}
      <div className="mt-4 flex items-center gap-2">
        <PrimaryButton
          onClick={save}
          icon={CheckCircle2}
          disabled={!pin}
          className="flex-1 justify-center"
        >
          {locating ? "Locating…" : "Save location"}
        </PrimaryButton>
        {pin && (
          <GhostButton onClick={clearPin} icon={X}>
            Clear pin
          </GhostButton>
        )}
      </div>
    </Modal>
  );
}
